import React, { useMemo } from "react"
import {
  Table,
  Tag,
  Space,
  Typography,
  message,
  Button,
  Dropdown,
} from "@mimber/ui-web"
import {
  ClockCircleOutlined,
  MoreOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@mimber/ui-web"
import { useGroupRoles } from "@mimber/client/mimber-organization"
import type { ColumnsType, TableProps } from "@mimber/ui-web"
import type { GroupRoleDto } from "@mimber/schema"
import type { GroupRolesTableProps } from "./GroupRolesTable.types"
import {
  handleTableChange,
  applyQueryParamsToColumns,
  getColumnSearchProps,
  formatTableDate,
} from "@mimber/ui-web"
import { useRememberedTableQueryParams } from "@/core/hooks"
import {
  getRememberedPaginationConfig,
  getRememberedTableLocale,
} from "@/utils"

const { Text } = Typography

export const GroupRolesTable: React.FunctionComponent<GroupRolesTableProps> = ({
  groupUuid,
  onDeleteRole,
  onEditRole,
  className,
  ...tableProps
}) => {
  const { queryParams, setQueryParams, resetQueryParams } =
    useRememberedTableQueryParams({
      tableId: "group-roles",
      defaultQueryParams: {
        page: 1,
        limit: 10,
      },
      scope: {
        groupUuid,
      },
    })

  const {
    data: response,
    isLoading,
    error,
  } = useGroupRoles(groupUuid, queryParams)

  // Show error message if query fails
  React.useEffect(() => {
    if (error) {
      message.error(`Failed to load group roles: ${error.message}`)
    }
  }, [error])

  const columns: ColumnsType<GroupRoleDto> = useMemo(
    () => [
      {
        title: "Role Name",
        dataIndex: "name",
        key: "name",
        render: (name: string, record) => (
          <Space>
            <Text strong>{name}</Text>
            {record.isLeader && <Tag color="blue">Leader</Tag>}
          </Space>
        ),
        sorter: true,
        sortDirections: ["ascend", "descend"],
        ...getColumnSearchProps("Search role"),
      },
      {
        title: "Description",
        dataIndex: "description",
        key: "description",
        render: (description: string | null) => (
          <Text type="secondary">{description || "No description"}</Text>
        ),
      },
      {
        title: "Sort Order",
        dataIndex: "sortOrder",
        key: "sortOrder",
        sorter: true,
        sortDirections: ["ascend", "descend"],
        render: (sortOrder: number) => <Text>{sortOrder}</Text>,
      },
      {
        title: "Created",
        dataIndex: "createdAt",
        key: "createdAt",
        sorter: true,
        sortDirections: ["ascend", "descend"],
        render: (createdAt: string) => (
          <Space>
            <ClockCircleOutlined />
            <Text type="secondary">{formatTableDate(createdAt)}</Text>
          </Space>
        ),
      },
      {
        title: "Actions",
        key: "actions",
        fixed: "right",
        width: 80,
        render: (_, record) => (
          <Dropdown
            menu={{
              items: [
                {
                  key: "edit",
                  label: "Edit",
                  icon: <EditOutlined />,
                  onClick: () => onEditRole?.(record),
                },
                {
                  key: "delete",
                  label: "Delete",
                  icon: <DeleteOutlined />,
                  danger: true,
                  onClick: () => onDeleteRole?.(record),
                },
              ],
            }}
            trigger={["click"]}
          >
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        ),
      },
    ],
    [onEditRole, onDeleteRole]
  )

  const onTableChange: TableProps<GroupRoleDto>["onChange"] = (
    pagination,
    filters,
    sorter
  ) => {
    const tableChangeData = {
      pagination,
      filters: filters as Record<string, string[] | null>,
      sorter: {
        field: Array.isArray(sorter) ? sorter[0]?.field : sorter?.field,
        order: Array.isArray(sorter) ? sorter[0]?.order : sorter?.order,
      } as { field?: string | string[]; order?: "ascend" | "descend" },
    }
    handleTableChange(tableChangeData, setQueryParams, columns)
  }

  const displayColumns = useMemo(
    () => applyQueryParamsToColumns(columns, queryParams),
    [columns, queryParams]
  )

  const paginationConfig = getRememberedPaginationConfig(
    response?.meta?.pagination?.total || 0,
    queryParams,
    resetQueryParams
  )

  return (
    <>
      <Table<GroupRoleDto>
        bordered
        columns={displayColumns}
        dataSource={response?.data || []}
        loading={isLoading}
        rowKey="uuid"
        pagination={paginationConfig}
        locale={getRememberedTableLocale(resetQueryParams, queryParams)}
        onChange={onTableChange}
        className={className}
        {...tableProps}
      />
    </>
  )
}
